import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, MoreHorizontal, Trash2, Edit, Star, UserCircle } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import { CountryDisplay } from '../ui/country-select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { SenderProfileForm } from './SenderProfileForm'
import { toast } from '../../hooks/useToast'
import { getApiError } from '../../lib/api'
import {
  useSenderProfiles,
  useCreateSenderProfile,
  useUpdateSenderProfile,
  useDeleteSenderProfile,
  useSetDefaultSenderProfile,
} from '../../hooks/useSettings'
import { SenderProfile, SenderProfileCreate, SenderProfileUpdate } from '../../types/settings'

export function SenderProfileList() {
  const { t } = useTranslation()
  const { data: profiles = [], isLoading } = useSenderProfiles()
  const createMutation = useCreateSenderProfile()
  const updateMutation = useUpdateSenderProfile()
  const deleteMutation = useDeleteSenderProfile()
  const setDefaultMutation = useSetDefaultSenderProfile()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingProfile, setEditingProfile] = useState<SenderProfile | null>(null)

  const handleCreate = async (data: SenderProfileCreate | SenderProfileUpdate) => {
    try {
      await createMutation.mutateAsync(data as SenderProfileCreate)
      toast({
        title: t('settings.senderProfiles.created'),
        description: t('settings.senderProfiles.createdDesc'),
      })
      setIsFormOpen(false)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleUpdate = async (data: SenderProfileCreate | SenderProfileUpdate) => {
    if (!editingProfile) return
    try {
      await updateMutation.mutateAsync({
        id: editingProfile.id,
        data: data as SenderProfileUpdate,
      })
      toast({
        title: t('settings.senderProfiles.updated'),
        description: t('settings.senderProfiles.updatedDesc'),
      })
      setEditingProfile(null)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('settings.senderProfiles.confirmDelete'))) return
    try {
      await deleteMutation.mutateAsync(id)
      toast({
        title: t('settings.senderProfiles.deleted'),
        description: t('settings.senderProfiles.deletedDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleSetDefault = async (id: number) => {
    try {
      await setDefaultMutation.mutateAsync(id)
      toast({
        title: t('settings.senderProfiles.defaultSet'),
        description: t('settings.senderProfiles.defaultSetDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60" />
            </CardHeader>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">{t('settings.senderProfiles.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('settings.senderProfiles.description')}
          </p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('settings.senderProfiles.add')}
        </Button>
      </div>

      {profiles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <UserCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">{t('settings.senderProfiles.empty')}</p>
            <Button className="mt-4" onClick={() => setIsFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('settings.senderProfiles.addFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {profiles.map((profile) => (
            <Card key={profile.id}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div className="space-y-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    {profile.name}
                    {profile.isDefault && (
                      <Badge variant="secondary">
                        <Star className="mr-1 h-3 w-3" />
                        {t('settings.default')}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>{profile.companyName}</CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditingProfile(profile)}>
                      <Edit className="mr-2 h-4 w-4" />
                      {t('common.edit')}
                    </DropdownMenuItem>
                    {!profile.isDefault && (
                      <DropdownMenuItem onClick={() => handleSetDefault(profile.id)}>
                        <Star className="mr-2 h-4 w-4" />
                        {t('settings.senderProfiles.setAsDefault')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => handleDelete(profile.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {profile.taxId && (
                    <div>
                      <span className="text-muted-foreground">
                        {t('settings.senderProfiles.taxId')}:{' '}
                      </span>
                      <span>{profile.taxId}</span>
                    </div>
                  )}
                  {(profile.street || profile.city || profile.postalCode || profile.countryCode) && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">
                        {t('settings.senderProfiles.address')}:{' '}
                      </span>
                      <span>
                        {profile.street}
                        {profile.city && `, ${profile.city}`}
                        {profile.postalCode && ` ${profile.postalCode}`}
                        {profile.countryCode && (
                          <>, <CountryDisplay alpha3={profile.countryCode} /></>
                        )}
                      </span>
                    </div>
                  )}
                  {profile.email && (
                    <div>
                      <span className="text-muted-foreground">
                        {t('settings.senderProfiles.email')}:{' '}
                      </span>
                      <span>{profile.email}</span>
                    </div>
                  )}
                  {profile.phone && (
                    <div>
                      <span className="text-muted-foreground">
                        {t('settings.senderProfiles.phone')}:{' '}
                      </span>
                      <span>{profile.phone}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <SenderProfileForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      <SenderProfileForm
        open={!!editingProfile}
        onClose={() => setEditingProfile(null)}
        onSubmit={handleUpdate}
        profile={editingProfile}
        isLoading={updateMutation.isPending}
      />
    </div>
  )
}
